'use client';
import React, { useState, useEffect } from 'react';
import Link from 'next/link';
import { usePathname } from 'next/navigation';

const navLinks = [
  { label: 'Home', href: '/' },
  { label: 'About Us', href: '/about' },
  { label: 'Services', href: '/services' },
  { label: 'Case Studies', href: '/case-studies' },
  { label: 'Clients', href: '/clients' },
  { label: 'Resources', href: '/resources' },
  { label: 'Blog', href: '/blog' },
  { label: 'Contact Us', href: '/contact' },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const pathname = usePathname();

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <>
      {/* Top Social Bar - Pinnacle1 style: solid blue #0091DD, 48px height */}
      <div
        className="fixed top-0 left-0 right-0 z-50 flex items-center"
        style={{ background: '#0091DD', height: '48px' }}
      >
        <div className="container-custom w-full">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <a
                href="https://www.linkedin.com"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="LinkedIn"
                className="flex items-center justify-center text-white hover:text-white/70 transition-colors duration-200"
              >
                <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M16 8a6 6 0 016 6v7h-4v-7a2 2 0 00-2-2 2 2 0 00-2 2v7h-4v-7a6 6 0 016-6zM2 9h4v12H2z" />
                  <circle cx="4" cy="4" r="2" />
                </svg>
              </a>
              <a
                href="https://www.facebook.com"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Facebook"
                className="flex items-center justify-center text-white hover:text-white/70 transition-colors duration-200"
              >
                <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M18 2h-3a5 5 0 00-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 011-1h3z" />
                </svg>
              </a>
            </div>
            <span className="text-white/90 text-xs hidden sm:block font-medium" style={{ fontFamily: 'Montserrat, sans-serif', letterSpacing: '0.05em' }}>
              Premier IT Staffing &amp; Consulting Partner
            </span>
          </div>
        </div>
      </div>

      {/* Main Navbar - Pinnacle1 style: solid #00A7FF, 96px height */}
      <nav
        className="fixed left-0 right-0 z-50 transition-all duration-300"
        style={{
          top: '48px',
          background: scrolled ? '#0091DD' : '#00A7FF',
          height: '96px',
          boxShadow: scrolled ? '0 4px 20px rgba(0,0,0,0.15)' : 'none',
        }}
      >
        <div className="container-custom h-full">
          <div className="flex items-center justify-between h-full">
            {/* Logo */}
            <Link href="/" className="flex items-center gap-3 group flex-shrink-0">
              <div
                className="flex items-center justify-center"
                style={{
                  width: '52px',
                  height: '52px',
                  background: 'rgba(255,255,255,0.15)',
                  border: '2px solid rgba(255,255,255,0.4)',
                }}
              >
                <span
                  className="font-bold text-white text-xl"
                  style={{ fontFamily: 'Poppins, sans-serif' }}
                >
                  TR
                </span>
              </div>
              <div className="flex flex-col">
                <span
                  className="font-bold text-white leading-tight"
                  style={{ fontFamily: 'Poppins, sans-serif', fontSize: 'clamp(14px, 2vw, 22px)', fontWeight: 700 }}
                >
                  TranceformRIGHT
                </span>
              </div>
            </Link>

            {/* Desktop Nav - Montserrat 18px weight 400 */}
            <div className="hidden lg:flex items-center gap-8">
              {navLinks?.map((link) => (
                <Link
                  key={link?.href}
                  href={link?.href}
                  className="transition-all duration-200 relative group"
                  style={{
                    fontFamily: 'Montserrat, sans-serif',
                    fontSize: '15px',
                    fontWeight: pathname === link?.href ? 600 : 400,
                    color: 'white',
                    opacity: pathname === link?.href ? 1 : 0.9,
                  }}
                >
                  {link?.label}
                  <span
                    className="absolute -bottom-1 left-0 h-0.5 bg-white transition-all duration-200"
                    style={{ width: pathname === link?.href ? '100%' : '0' }}
                  />
                  <span
                    className="absolute -bottom-1 left-0 h-0.5 bg-white w-0 group-hover:w-full transition-all duration-200"
                    style={{ display: pathname === link?.href ? 'none' : 'block' }}
                  />
                </Link>
              ))}
            </div>

            {/* Mobile Menu Button */}
            <button
              className="lg:hidden p-2"
              onClick={() => setMobileOpen(!mobileOpen)}
              aria-label="Toggle menu"
            >
              <div className="w-6 flex flex-col gap-1.5">
                <span className={`block h-0.5 bg-white transition-all duration-300 ${mobileOpen ? 'rotate-45 translate-y-2' : ''}`} />
                <span className={`block h-0.5 bg-white transition-all duration-300 ${mobileOpen ? 'opacity-0' : ''}`} />
                <span className={`block h-0.5 bg-white transition-all duration-300 ${mobileOpen ? '-rotate-45 -translate-y-2' : ''}`} />
              </div>
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        <div
          className={`lg:hidden transition-all duration-300 overflow-hidden ${
            mobileOpen ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'
          }`}
          style={{ background: '#0091DD', borderTop: '1px solid rgba(255,255,255,0.2)' }}
        >
          <div className="container-custom py-4 flex flex-col gap-1">
            {navLinks?.map((link) => (
              <Link
                key={link?.href}
                href={link?.href}
                onClick={() => setMobileOpen(false)}
                className="px-4 py-3 text-sm transition-all duration-200"
                style={{
                  fontFamily: 'Montserrat, sans-serif',
                  fontWeight: pathname === link?.href ? 600 : 400,
                  color: 'white',
                  background: pathname === link?.href ? 'rgba(255,255,255,0.15)' : 'transparent',
                }}
              >
                {link?.label}
              </Link>
            ))}
          </div>
        </div>
      </nav>
    </>
  );
}
